import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { getActiveAiTable, getAiTable, normalizeAiTableSnapshot, AiTableColumn, AiTableSnapshot } from '../hooks/useAiTableRegistry';
import { hebrewPdfFont } from './csvManipulator';
import { heb } from '../views/orders/orderTouch/localPdf';
import JSDB from '../fromKotlin/nk';

export type AiTableExportFormat = 'xlsx' | 'pdf' | 'print';

export interface ExportTablePayload {
    tableId?: string;
    format?: AiTableExportFormat;
    filename?: string;
    title?: string;
    columns?: Array<string | { key?: string; id?: string; label?: string; header?: string }>;
    rows?: Array<Record<string, unknown> | unknown[]>;
    maxRows?: number;
}

export interface ExportTableResult {
    filename: string;
    format: AiTableExportFormat;
    rowCount: number;
    totalRows: number;
    truncated: boolean;
}

const PDF_FONT_NAME = 'tahoma';

function safeFilename(value: unknown, fallback: string): string {
    const withoutExtension = String(value || fallback)
        .replace(/\.(xlsx|pdf)$/i, '')
        .replace(/[<>:"/\\|?*\u0000-\u001f]/g, '-')
        .replace(/\s+/g, ' ')
        .replace(/^\.+|\.+$/g, '')
        .trim()
        .slice(0, 120);
    return withoutExtension || fallback;
}

function inlineSnapshot(payload: ExportTablePayload): AiTableSnapshot {
    const rows = Array.isArray(payload.rows) ? payload.rows : [];
    let columns: AiTableColumn[] = (payload.columns || []).map((column, index) => {
        if (typeof column === 'string') return { key: column, label: column };
        const key = String(column?.key || column?.id || `column_${index + 1}`);
        return { key, label: String(column?.label || column?.header || key) };
    });
    if (!columns.length && Array.isArray(rows[0])) {
        columns = rows[0].map((_, index) => ({
            key: `column_${index + 1}`,
            label: `עמודה ${index + 1}`,
        }));
    }
    return normalizeAiTableSnapshot(
        { columns, rows, totalRows: rows.length },
        { maxRows: payload.maxRows },
    );
}

function resolveSnapshot(payload: ExportTablePayload): { snapshot: AiTableSnapshot; title: string } {
    if (payload.tableId) {
        const table = getAiTable(payload.tableId);
        if (!table) throw new Error('הטבלה המבוקשת אינה זמינה עוד');
        return { snapshot: table.getSnapshot(), title: payload.title || table.metadata.title };
    }
    if (payload.columns || payload.rows) {
        return { snapshot: inlineSnapshot(payload), title: payload.title || 'ייצוא טבלה' };
    }
    const table = getActiveAiTable();
    if (!table) throw new Error('לא נמצאה טבלה פעילה לייצוא');
    return { snapshot: table.getSnapshot(), title: payload.title || table.metadata.title };
}

function exportXlsx(snapshot: AiTableSnapshot, filename: string, title: string): void {
    const values = [
        snapshot.columns.map((column) => column.label),
        ...snapshot.rows,
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(values);
    worksheet['!cols'] = snapshot.columns.map((column, index) => {
        const longest = snapshot.rows.reduce(
            (length, row) => Math.max(length, String(row[index] ?? '').length),
            column.label.length,
        );
        return { wch: Math.min(60, Math.max(10, longest + 2)) };
    });
    (worksheet as any)['!views'] = [{ RTL: true }];
    const workbook = XLSX.utils.book_new();
    const sheetName = safeFilename(title, 'טבלה').slice(0, 31);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    XLSX.writeFile(workbook, `${filename}.xlsx`);
}

function exportPdf(snapshot: AiTableSnapshot, filename: string, title: string): void {
    const doc = new jsPDF(snapshot.columns.length > 5 ? 'landscape' : 'portrait', 'pt', 'A4');
    doc.addFileToVFS('tahoma-normal.ttf', hebrewPdfFont);
    doc.addFont('tahoma-normal.ttf', PDF_FONT_NAME, 'normal');
    doc.setFont(PDF_FONT_NAME);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const user = JSDB().getUser?.()?.first;
    const companyName = String(user?.user_company_print_name || JSDB().getCompany?.() || '').trim();
    const businessNumber = String(user?.user_buisness_number || '').trim();
    const issuedAt = new Date().toLocaleString('he-IL');
    doc.setProperties({ title, author: companyName || 'NiHulKav' });

    let headerY = 28;
    if (companyName) {
        doc.setFontSize(13);
        doc.text(heb(companyName), pageWidth - 32, headerY, { align: 'right' });
        if (businessNumber) {
            doc.setFontSize(9);
            doc.text(heb(`ח.פ / עוסק: ${businessNumber}`), 32, headerY, { align: 'left' });
        }
        headerY += 20;
    }
    doc.setFontSize(16);
    doc.text(heb(title), pageWidth - 32, headerY, { align: 'right' });
    doc.setFontSize(9);
    doc.setTextColor(90);
    doc.text(heb(`הופק: ${issuedAt}`), pageWidth - 32, headerY + 15, { align: 'right' });
    if (snapshot.truncated) {
        doc.text(
            heb(`מוצגות ${snapshot.rows.length} מתוך ${snapshot.totalRows} שורות`),
            32,
            headerY + 15,
            { align: 'left' },
        );
    }
    doc.setDrawColor(180);
    doc.line(32, headerY + 23, pageWidth - 32, headerY + 23);
    doc.setTextColor(20);

    const reversedColumns = snapshot.columns.slice().reverse();
    const sourceIndexes = reversedColumns.map((column) =>
        snapshot.columns.findIndex((candidate) => candidate.key === column.key));
    (doc as any).autoTable({
        startY: headerY + 32,
        head: [reversedColumns.map((column) => heb(column.label))],
        body: snapshot.rows.map((row) => sourceIndexes.map((index) => heb(row[index]))),
        margin: { left: 28, right: 28 },
        styles: {
            font: PDF_FONT_NAME,
            fontSize: 9,
            halign: 'right',
            cellPadding: 4,
            overflow: 'linebreak',
        },
        headStyles: {
            fillColor: [49, 111, 167],
            textColor: 255,
            fontStyle: 'normal',
            halign: 'right',
        },
        alternateRowStyles: { fillColor: [244, 247, 250] },
        didDrawPage: () => {
            doc.setFont(PDF_FONT_NAME);
            doc.setFontSize(8);
            doc.setTextColor(100);
            const page = (doc as any).internal.getNumberOfPages();
            doc.text(heb(`עמוד ${page}`), pageWidth / 2, pageHeight - 18, { align: 'center' });
            doc.setTextColor(20);
        },
    });
    doc.save(`${filename}.pdf`);
}

function escapeHtml(value: unknown): string {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
}

function printTable(snapshot: AiTableSnapshot, title: string): void {
    const frame = document.createElement('iframe');
    frame.setAttribute('title', 'print-table');
    frame.style.position = 'fixed';
    frame.style.width = '1px';
    frame.style.height = '1px';
    frame.style.right = '0';
    frame.style.bottom = '0';
    frame.style.border = '0';
    frame.style.opacity = '0';
    const headers = snapshot.columns.map((column) => `<th>${escapeHtml(column.label)}</th>`).join('');
    const rows = snapshot.rows
        .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`)
        .join('');
    frame.srcdoc = `<!doctype html>
<html dir="rtl" lang="he">
<head>
<meta charset="utf-8">
<title>${escapeHtml(title)}</title>
<style>
@page { size: landscape; margin: 12mm; }
body { direction: rtl; font-family: Tahoma, Arial, sans-serif; color: #111; }
h1 { font-size: 18px; margin: 0 0 12px; text-align: right; }
table { width: 100%; border-collapse: collapse; table-layout: auto; }
th, td { border: 1px solid #aaa; padding: 5px 7px; text-align: right; word-break: break-word; }
th { background: #316fa7; color: white; font-weight: 600; }
tbody tr:nth-child(even) { background: #f4f7fa; }
</style>
</head>
<body><h1>${escapeHtml(title)}</h1><table><thead><tr>${headers}</tr></thead><tbody>${rows}</tbody></table></body>
</html>`;
    frame.onload = () => {
        const printWindow = frame.contentWindow;
        if (!printWindow) {
            frame.remove();
            return;
        }
        printWindow.onafterprint = () => frame.remove();
        printWindow.focus();
        printWindow.print();
        window.setTimeout(() => frame.remove(), 60_000);
    };
    document.body.appendChild(frame);
}

export async function exportAiTable(payload: ExportTablePayload): Promise<ExportTableResult> {
    const { snapshot, title } = resolveSnapshot(payload || {});
    if (!snapshot.columns.length) throw new Error('אין עמודות זמינות לייצוא');
    const format: AiTableExportFormat =
        payload?.format === 'pdf' || payload?.format === 'print' ? payload.format : 'xlsx';
    const filename = safeFilename(payload?.filename, title || 'table-export');

    if (format === 'pdf') exportPdf(snapshot, filename, title);
    else if (format === 'print') printTable(snapshot, title);
    else exportXlsx(snapshot, filename, title);

    return {
        filename: format === 'print' ? filename : `${filename}.${format}`,
        format,
        rowCount: snapshot.rows.length,
        totalRows: snapshot.totalRows,
        truncated: snapshot.truncated,
    };
}
